import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Alert, Badge, Button, Spinner } from 'react-bootstrap';
import { AlertTriangle, Check, Languages, Layers, Package, Plus, Trash2 } from 'lucide-react';
import { ImageKonvaEditor } from './ImageKonvaEditor';
import type { ImageEditorTool } from '../types';
import { BADGE_SMALL_STYLE } from '../shared/styles';
import { convertToCloudFrontUrl } from '../utils/imageUtils';

interface PDPImageEditorProps {
  readonly detailedImages: string[];
  readonly selectedPdpImages: string[];
  readonly imageDimensions: Record<string, { width: number; height: number }>;
  readonly translatingImages: Set<string>;
  readonly draggedPDPIndex: number | null;
  readonly preloadedImages: Record<string, HTMLImageElement>;
  readonly selectedTool: ImageEditorTool;
  readonly textSettings?: { color: string; fontSize: number } | undefined;
  readonly imageSelection?: ImageSelection;
  readonly matchedProduct?: any;
  readonly onPDPToggle: (imageUrl: string) => void;
  readonly onTranslateImage: (url: string) => Promise<void>;
  readonly onDeleteDetailedImage: (url: string) => void;
  readonly onDeletePdpImage?: ((url: string) => Promise<void>) | undefined;
  readonly onSaveEditedImage?: (originalImageUrl: string, editedImageUrl: string) => void;
  readonly onPDPDragStart: (index: number) => void;
  readonly onPDPDragOver: (e: React.DragEvent, index: number) => void;
  readonly onPDPDragEnd: () => void;
  readonly onPaste: (e: React.ClipboardEvent) => void;
  readonly onPreviewImage: (url: string) => void;
  readonly onImageSelectionChange?: (selection: ImageSelection) => void;
  readonly onSavePdpImages?: () => Promise<void>;
}

export interface PDPImageEditorRef {
  savePdpImages: () => Promise<void>;
  saveLoading: boolean;
  saveMessage: { type: 'success' | 'error'; text: string } | null;
}

interface ImageSelection {
  thumbnails: Record<string, string>;
  gallery: string[];
  pdpImages: string[];
}

export const PDPImageEditor = forwardRef<PDPImageEditorRef, PDPImageEditorProps>(
  (
    {
      detailedImages,
      selectedPdpImages,
      imageDimensions,
      translatingImages,
      draggedPDPIndex,
      preloadedImages,
      selectedTool,
      textSettings,
      matchedProduct,
      imageSelection,
      onPDPToggle,
      onTranslateImage,
      onDeleteDetailedImage,
      onDeletePdpImage,
      // onSaveEditedImage - not used anymore, editors manage their own state
      onPDPDragStart,
      onPDPDragOver,
      onPDPDragEnd,
      onPaste,
      onPreviewImage,
      onImageSelectionChange,
      onSavePdpImages,
    }: PDPImageEditorProps,
    ref,
  ) => {
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const imageEditorRefs = useRef<Map<string, React.MutableRefObject<(() => string) | undefined>>>(new Map());
    const imageKeysRef = useRef<Map<number, string>>(new Map());
    const previousToolRef = useRef<ImageEditorTool>(selectedTool);

    // Preserve edited images when switching from editing tools to select tool
    useEffect(() => {
      const isEditingTool = ['rect-cutout', 'erase', 'text', 'translate'].includes(selectedTool);
      const wasEditingTool = ['rect-cutout', 'erase', 'text', 'translate'].includes(previousToolRef.current);

      // If switching from editing tool to non-editing tool (e.g., select)
      if (wasEditingTool && !isEditingTool) {
        console.info('🔄 Switching from editing tool to select, preserving edits...');

        // Get current edited images from refs
        const updatedPdpImages = selectedPdpImages.map((url, idx) => {
          const stableKey = imageKeysRef.current.get(idx) || `pdp-slot-${idx}`;
          const ref = imageEditorRefs.current.get(stableKey);
          if (ref?.current) {
            const editedUrl = ref.current();
            if (editedUrl && editedUrl !== url) {
              console.info(`✅ Preserving edit for image ${idx}:`, {
                original: url.substring(0, 60),
                edited: editedUrl.substring(0, 60),
              });
              return editedUrl;
            }
          }
          return url;
        });

        // Update imageSelection if any images were edited
        if (JSON.stringify(updatedPdpImages) !== JSON.stringify(selectedPdpImages)) {
          if (onImageSelectionChange && imageSelection) {
            onImageSelectionChange({
              ...imageSelection,
              pdpImages: updatedPdpImages,
            });
          }
        }
      }

      previousToolRef.current = selectedTool;
    }, [selectedTool, selectedPdpImages, imageSelection, onImageSelectionChange]);

    const handleSavePdpImages = async () => {
      if (!matchedProduct) {
        alert('Product information is missing');
        return;
      }

      if (selectedPdpImages.length === 0) {
        alert('No PDP images selected to save');
        return;
      }

      setSaveLoading(true);
      setSaveMessage(null);

      try {
        if (onSavePdpImages) {
          await onSavePdpImages();
          setSaveMessage({ type: 'success', text: 'PDP images saved successfully!' });
        } else {
          // Save to VerifiedImages with /en/ prefix (required for PDP images)
          const { verifiedImagesClient } = await import('../../../../../../infrastructure/VerifiedImagesClient');

          // Process each image: upload to S3 with /en/ prefix if needed
          const imagesToSave: string[] = [];
          const imagesToDelete: string[] = [];

          // Get current edited images from refs
          const currentImages = selectedPdpImages.map((url, idx) => {
            const stableKey = imageKeysRef.current.get(idx) || `pdp-slot-${idx}`;
            const ref = imageEditorRefs.current.get(stableKey);
            if (ref?.current) {
              const editedUrl = ref.current();
              return { original: url, edited: editedUrl || url };
            }
            return { original: url, edited: url };
          });

          console.info('📦 Processing PDP images for S3 upload:', currentImages);

          for (const { original, edited } of currentImages) {
            // If it's a data URL (edited image), upload to S3 with UUID filename
            if (edited.startsWith('data:')) {
              console.info('⬆️ Uploading edited image (data URL) to S3...');
              const uuid = crypto.randomUUID().replace(/-/g, '');
              const fileName = `en/pdp_${uuid}.webp`;

              const presignedResponse = await verifiedImagesClient.getPresignedUrl({
                gpProductId: matchedProduct.gpProductId,
                cpProductId: matchedProduct.cpProductId,
                cpItemId: matchedProduct.cpItemId,
                fileName,
              });

              if (!presignedResponse.success) {
                throw new Error(`Failed to get presigned URL: ${presignedResponse.message}`);
              }

              console.info('✅ Got presigned URL:', presignedResponse.uploadUrl.substring(0, 100));

              // Convert data URL to Blob
              const blob = await fetch(edited).then((res) => res.blob());
              const uploadSuccess = await verifiedImagesClient.uploadToS3(presignedResponse.uploadUrl, blob);

              if (!uploadSuccess) {
                throw new Error('Failed to upload image to S3');
              }

              // Convert S3 URL to CloudFront URL for storage
              const cloudFrontUrl = convertToCloudFrontUrl(presignedResponse.s3Url);
              console.info('✅ Uploaded edited image to S3, CloudFront URL:', cloudFrontUrl);
              imagesToSave.push(cloudFrontUrl);

              // If the original was from VerifiedImages (/en/pdp_), mark it for deletion
              if (!original.startsWith('data:') && original.includes('/en/pdp_')) {
                const originalCloudFrontUrl = convertToCloudFrontUrl(original);
                imagesToDelete.push(originalCloudFrontUrl);
                console.info('🗑️ Marking original image for deletion:', originalCloudFrontUrl);
              }
            } else {
              // Existing URL (not edited) - keep as is
              // No need to re-upload if it's already on S3
              const cloudFrontUrl = convertToCloudFrontUrl(edited);
              console.info('✅ Keeping existing image URL (not edited):', cloudFrontUrl);
              imagesToSave.push(cloudFrontUrl);
            }
          }

          // Delete old images first to avoid duplicates
          if (imagesToDelete.length > 0) {
            console.info('🗑️ Deleting old images from VerifiedImages:', imagesToDelete);
            const deleteResult = await verifiedImagesClient.deleteVerifiedImages({
              gpProductId: matchedProduct.gpProductId,
              cpProductId: matchedProduct.cpProductId,
              cpItemId: matchedProduct.cpItemId,
              imageUrls: imagesToDelete,
            });

            if (!deleteResult.success) {
              console.warn('⚠️ Failed to delete old images, continuing anyway:', deleteResult.message);
            } else {
              console.info('✅ Old images deleted successfully');
            }
          }

          console.info('💾 Saving to VerifiedImages:', imagesToSave);

          const result = await verifiedImagesClient.updateVerifiedImages({
            gpProductId: matchedProduct.gpProductId,
            cpProductId: matchedProduct.cpProductId,
            cpItemId: matchedProduct.cpItemId,
            imageUrlToAdd: imagesToSave,
          });

          if (result.success) {
            console.info('✅ PDP images saved to VerifiedImages successfully');
            setSaveMessage({ type: 'success', text: `Successfully saved ${imagesToSave.length} PDP image(s)!` });

            // Update imageSelection to use the saved CloudFront URLs
            if (onImageSelectionChange && imageSelection) {
              onImageSelectionChange({
                ...imageSelection,
                pdpImages: imagesToSave,
              });
            }
          } else {
            throw new Error(result.message || 'Failed to save PDP images');
          }
        }
      } catch (error) {
        console.error('Failed to save PDP images:', error);
        setSaveMessage({
          type: 'error',
          text: `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`,
        });
      } finally {
        setSaveLoading(false);
      }
    };

    // Expose save function and state to parent component
    useImperativeHandle(ref, () => ({
      savePdpImages: handleSavePdpImages,
      saveLoading,
      saveMessage,
    }));

    if (detailedImages.length === 0) {
      return (
        <Alert variant="warning">
          <AlertTriangle size={18} className="me-2" />
          No detailed images found. Please check if the product has detailed images synced from the media sync step.
        </Alert>
      );
    }

    return (
      <div style={{ display: 'flex', gap: '20px', height: '100vh' }}>
        <div
          style={{
            flex: 1,
            background: '#f8f9fa',
            padding: '20px',
            overflowY: 'scroll',
            border: '1px solid #dee2e6',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="mb-3">
            <h6 className="mb-0">
              <Package size={16} className="me-2" />
              Original Detailed Images
            </h6>
            <small className="text-muted">Full size - Click to preview</small>
          </div>

          <div className="d-flex flex-column gap-3">
            {detailedImages.map((url, idx) => {
              const dimensions = imageDimensions[url];
              const showTranslateButton = !dimensions || dimensions.height <= 1024;

              return (
                <div
                  key={`original-${idx}`}
                  style={{
                    background: 'white',
                    overflow: 'hidden',
                    border: '1px solid #dee2e6',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => onPreviewImage(url)}
                >
                  <img
                    src={url}
                    alt={`Original ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      objectFit: 'contain',
                      background: '#f8f9fa',
                    }}
                  />
                  <div
                    style={{
                      padding: '12px',
                      borderTop: '1px solid #dee2e6',
                      background: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      <Badge bg="secondary" style={BADGE_SMALL_STYLE}>
                        Image {idx + 1}
                      </Badge>
                      {dimensions && (
                        <Badge bg="light" text="dark" style={{ ...BADGE_SMALL_STYLE, marginLeft: '4px' }}>
                          {dimensions.width}×{dimensions.height}
                        </Badge>
                      )}
                    </div>
                    <div className="d-flex gap-1">
                      <Button
                        variant={selectedPdpImages.includes(url) ? 'success' : 'outline-secondary'}
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPDPToggle(url);
                        }}
                        title={selectedPdpImages.includes(url) ? 'Selected' : 'Add to edit area'}
                      >
                        {selectedPdpImages.includes(url) ? <Check size={14} /> : <Plus size={14} />}
                      </Button>
                      {showTranslateButton && (
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await onTranslateImage(url);
                          }}
                          disabled={translatingImages.has(url)}
                          title="Translate and add to edit area"
                        >
                          {translatingImages.has(url) ? (
                            <Spinner animation="border" size="sm" style={{ width: '14px', height: '14px' }} />
                          ) : (
                            <Languages size={14} />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Delete this image permanently from detailed images?')) {
                            onDeleteDetailedImage(url);
                            onPDPToggle(url);
                            console.info('Deleted detailed image:', url);
                          }
                        }}
                        title="Delete image permanently"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          onPaste={onPaste}
          tabIndex={0}
          style={{
            flex: 1,
            background: '#f8f9fa',
            padding: '20px',
            overflowY: 'scroll',
            border: '1px solid #dee2e6',
            display: 'flex',
            flexDirection: 'column',
            outline: 'none',
          }}
        >
          <div className="mb-3">
            <h6 className="mb-0">
              <Layers size={16} className="me-2" />
              Edit Area
            </h6>
          </div>

          {selectedPdpImages.length === 0 ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '2px dashed #dee2e6',
                overflow: 'hidden',
              }}
            >
              <div className="text-center text-muted">
                <Package size={64} className="mb-3 opacity-25" />
                <h5>No images copied</h5>
                <p className="mb-2">Click the button on images from the left</p>
                <p className="mb-0">
                  <strong>OR</strong> press <Badge bg="info">Ctrl+V</Badge> / <Badge bg="info">Cmd+V</Badge> to paste
                  from clipboard
                </p>
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3" style={{ flex: 1 }}>
              {selectedPdpImages.map((url, idx) => (
                <div key={`selected-pdp-${url}-${idx}`}>
                  {selectedTool === 'rect-cutout' ||
                  selectedTool === 'erase' ||
                  selectedTool === 'text' ||
                  selectedTool === 'translate' ? (
                    <div
                      style={{
                        background: 'white',
                        overflow: 'hidden',
                        border: '1px solid #dee2e6',
                        transition: 'all 0.2s',
                      }}
                    >
                      <ImageKonvaEditor
                        key={(() => {
                          // Maintain stable key for each image position
                          if (!imageKeysRef.current.has(idx)) {
                            // Use original URL or create stable ID
                            const stableKey = url.startsWith('data:') ? `pdp-slot-${idx}` : url;
                            imageKeysRef.current.set(idx, stableKey);
                          }
                          return imageKeysRef.current.get(idx)!;
                        })()}
                        imageUrl={url}
                        preloadedImage={preloadedImages[url]}
                        tool={selectedTool}
                        textSettings={textSettings}
                        getCurrentEditedImage={(() => {
                          // Use stable key to maintain history
                          const stableKey = imageKeysRef.current.get(idx) || `pdp-slot-${idx}`;
                          if (!imageEditorRefs.current.has(stableKey)) {
                            imageEditorRefs.current.set(stableKey, { current: undefined });
                          }
                          return imageEditorRefs.current.get(stableKey)!;
                        })()}
                        renderActions={() => (
                          <div style={{ display: 'flex', gap: '8px', marginLeft: '8px' }}>
                            <Button
                              variant="info"
                              size="sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await onTranslateImage(url);
                              }}
                              disabled={translatingImages.has(url)}
                              title="Translate Korean to English (full image)"
                              style={{
                                width: '28px',
                                height: '28px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {translatingImages.has(url) ? (
                                <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />
                              ) : (
                                <Languages size={14} />
                              )}
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (onDeletePdpImage) {
                                  await onDeletePdpImage(url);
                                } else {
                                  onPDPToggle(url);
                                }
                              }}
                              title="Remove image"
                              style={{
                                width: '28px',
                                height: '28px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        )}
                      />
                    </div>
                  ) : (
                    <div
                      draggable
                      onDragStart={() => onPDPDragStart(idx)}
                      onDragOver={(e) => onPDPDragOver(e, idx)}
                      onDragEnd={onPDPDragEnd}
                      style={{
                        background: 'white',
                        overflow: 'hidden',
                        border: '1px solid #dee2e6',
                        cursor: 'move',
                        opacity: draggedPDPIndex === idx ? 0.5 : 1,
                        transition: 'all 0.2s',
                        position: 'relative',
                      }}
                    >
                      <img
                        src={url}
                        alt={`Selected ${idx + 1}`}
                        style={{
                          width: '100%',
                          height: 'auto',
                          minHeight: '40px',
                          objectFit: 'contain',
                          display: 'block',
                          background: '#f8f9fa',
                        }}
                      />
                      <div
                        style={{
                          padding: '8px 12px',
                          borderTop: '1px solid #dee2e6',
                          background: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '0.85rem',
                            color: '#495057',
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                            flex: 1,
                          }}
                        >
                          {url.startsWith('data:') ? 'Edited (not saved)' : url.split('/').pop()}
                        </span>
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                          <Button
                            variant="info"
                            size="sm"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await onTranslateImage(url);
                            }}
                            disabled={translatingImages.has(url)}
                            title="Translate Korean to English"
                            style={{
                              width: '28px',
                              height: '28px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {translatingImages.has(url) ? (
                              <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />
                            ) : (
                              <Languages size={14} />
                            )}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (onDeletePdpImage) {
                                await onDeletePdpImage(url);
                              } else {
                                onPDPToggle(url);
                              }
                            }}
                            title="Remove image"
                            style={{
                              width: '28px',
                              height: '28px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  },
);
